import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, CalendarCheck, MailQuestion, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("shamar_token") || ""}` });

const STATUS_LABELS = {
  confirmed: { label: "Confirmée", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-600/30" },
  pending_payment: { label: "Paiement en attente", cls: "bg-amber-500/15 text-amber-700 border-amber-600/30" },
  expired: { label: "Expirée", cls: "bg-gray-400/15 text-gray-500 border-gray-400/30" },
  payment_failed: { label: "Paiement échoué", cls: "bg-red-500/15 text-red-700 border-red-600/30" },
  cancelled: { label: "Annulée", cls: "bg-gray-400/15 text-gray-500 border-gray-400/30" },
  refunded: { label: "Remboursée", cls: "bg-blue-500/15 text-blue-700 border-blue-600/30" },
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("shamar_token", data.access_token);
      onLogin(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="noise-overlay" />
      <form onSubmit={submit} className="relative max-w-md w-full bg-white p-10 md:p-12" data-testid="admin-login-form">
        <img src="/logo-shamar.png" alt="Shamar" className="h-16 w-auto mx-auto mb-6" />
        <h1 className="font-serif-lux text-2xl text-[#0A192F] text-center mb-8">Espace administration</h1>
        <div className="space-y-6">
          <input
            required
            type="email"
            className="lux-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email"
          />
          <input
            required
            type="password"
            className="lux-input"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full px-8 py-4 bg-[#0A192F] text-[#F0F0F0] text-[13px] tracking-[0.16em] uppercase hover:bg-[#C9A03C] hover:text-[#050B14] transition-colors duration-400 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [b, q] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeaders() }),
        axios.get(`${API}/admin/quotes`, { headers: authHeaders() }),
      ]);
      setBookings(b.data);
      setQuotes(q.data);
    } catch {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const logout = () => {
    localStorage.removeItem("shamar_token");
    axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Annuler la réservation de ${b.nom} (${b.booking_date} à ${b.time_slot}) ?`)) return;
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/cancel`, {}, { headers: authHeaders() });
      toast.success("Réservation annulée");
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Annulation impossible");
    }
  };

  const refundBooking = async (b) => {
    if (!window.confirm(`Rembourser ${(b.amount || 0).toFixed(0)} € à ${b.nom} via Stripe ? Cette action est définitive.`)) return;
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/refund`, {}, { headers: authHeaders() });
      toast.success("Remboursement effectué");
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Remboursement impossible");
    }
  };

  if (checking)
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A03C] animate-spin" />
      </div>
    );
  if (!user) return <LoginForm onLogin={setUser} />;

  const confirmed = bookings.filter((b) => b.status === "confirmed");
  const revenue = confirmed.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <header className="bg-[#0A192F] px-6 lg:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-white px-3 py-2">
            <img src="/logo-shamar.png" alt="Shamar" className="h-9 w-auto" />
          </div>
          <span className="text-[#F0F0F0]/60 text-sm font-light hidden sm:block">Espace administration</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[#C9A03C] text-sm font-light hidden sm:block">{user.email}</span>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-[#F0F0F0]/70 hover:text-[#C9A03C] text-sm transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid sm:grid-cols-3 gap-6 mb-10">
          <div className="bg-white border border-black/5 p-6" data-testid="admin-stat-bookings">
            <p className="text-xs tracking-[0.18em] uppercase text-[#0A192F]/50 mb-2">Réservations confirmées</p>
            <p className="font-serif-lux text-4xl text-[#0A192F]">{confirmed.length}</p>
          </div>
          <div className="bg-white border border-black/5 p-6" data-testid="admin-stat-revenue">
            <p className="text-xs tracking-[0.18em] uppercase text-[#0A192F]/50 mb-2">Encaissé (confirmé)</p>
            <p className="font-serif-lux text-4xl text-[#C9A03C]">{revenue.toFixed(0)} €</p>
          </div>
          <div className="bg-white border border-black/5 p-6" data-testid="admin-stat-quotes">
            <p className="text-xs tracking-[0.18em] uppercase text-[#0A192F]/50 mb-2">Demandes de devis</p>
            <p className="font-serif-lux text-4xl text-[#0A192F]">{quotes.length}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setTab("bookings")}
              data-testid="admin-tab-bookings"
              className={`flex items-center gap-2 px-5 py-2.5 text-[13px] tracking-[0.1em] uppercase border transition-colors duration-300 ${
                tab === "bookings" ? "bg-[#0A192F] text-[#F0F0F0] border-[#0A192F]" : "bg-white text-[#0A192F]/70 border-black/10 hover:border-[#C9A03C]"
              }`}
            >
              <CalendarCheck className="w-4 h-4" /> Réservations ({bookings.length})
            </button>
            <button
              onClick={() => setTab("quotes")}
              data-testid="admin-tab-quotes"
              className={`flex items-center gap-2 px-5 py-2.5 text-[13px] tracking-[0.1em] uppercase border transition-colors duration-300 ${
                tab === "quotes" ? "bg-[#0A192F] text-[#F0F0F0] border-[#0A192F]" : "bg-white text-[#0A192F]/70 border-black/10 hover:border-[#C9A03C]"
              }`}
            >
              <MailQuestion className="w-4 h-4" /> Devis ({quotes.length})
            </button>
          </div>
          <button
            onClick={loadData}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 text-sm text-[#0A192F]/60 hover:text-[#C9A03C] transition-colors duration-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>

        <div className="bg-white border border-black/5 overflow-x-auto">
          {tab === "bookings" ? (
            <table className="w-full text-sm" data-testid="admin-bookings-table">
              <thead>
                <tr className="border-b border-black/5 text-left text-xs tracking-[0.12em] uppercase text-[#0A192F]/50">
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Prestation</th>
                  <th className="px-5 py-4">Date & créneau</th>
                  <th className="px-5 py-4">Montant</th>
                  <th className="px-5 py-4">Statut</th>
                  <th className="px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#0A192F]/40 font-light">Aucune réservation pour le moment</td></tr>
                )}
                {bookings.map((b) => {
                  const st = STATUS_LABELS[b.status] || { label: b.status, cls: "bg-gray-100 text-gray-600 border-gray-300" };
                  return (
                    <tr key={b.id} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                      <td className="px-5 py-4">
                        <p className="text-[#0A192F] font-medium">{b.nom}</p>
                        <p className="text-[#0A192F]/50 font-light text-xs">{b.email}{b.telephone ? ` · ${b.telephone}` : ""}</p>
                      </td>
                      <td className="px-5 py-4 text-[#0A192F]/80 font-light">{b.service_label}</td>
                      <td className="px-5 py-4 text-[#0A192F]/80 font-light">{b.booking_date} · {b.time_slot}</td>
                      <td className="px-5 py-4 text-[#C9A03C] font-medium">{(b.amount || 0).toFixed(0)} € <span className="text-[#0A192F]/40 text-xs font-light">{b.payment_option === "acompte" ? "acompte" : "total"}</span></td>
                      <td className="px-5 py-4"><span className={`inline-block px-3 py-1 text-xs border ${st.cls}`}>{st.label}</span></td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          {["pending_payment", "confirmed"].includes(b.status) && (
                            <button
                              onClick={() => cancelBooking(b)}
                              data-testid={`admin-cancel-${b.id}`}
                              className="px-3 py-1.5 text-xs border border-black/15 text-[#0A192F]/70 hover:border-red-400 hover:text-red-600 transition-colors duration-300"
                            >
                              Annuler
                            </button>
                          )}
                          {b.status === "confirmed" && (
                            <button
                              onClick={() => refundBooking(b)}
                              data-testid={`admin-refund-${b.id}`}
                              className="px-3 py-1.5 text-xs border border-[#C9A03C]/40 text-[#C9A03C] hover:bg-[#C9A03C] hover:text-white transition-colors duration-300"
                            >
                              Rembourser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="admin-quotes-table">
              <thead>
                <tr className="border-b border-black/5 text-left text-xs tracking-[0.12em] uppercase text-[#0A192F]/50">
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Bien</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4">Reçu le</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-[#0A192F]/40 font-light">Aucune demande de devis pour le moment</td></tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    <td className="px-5 py-4">
                      <p className="text-[#0A192F] font-medium">{q.nom}</p>
                      <p className="text-[#0A192F]/50 font-light text-xs">{q.email}{q.telephone ? ` · ${q.telephone}` : ""}</p>
                    </td>
                    <td className="px-5 py-4 text-[#0A192F]/80 font-light">{q.type_bien} — {q.localisation}</td>
                    <td className="px-5 py-4 text-[#0A192F]/80 font-light">{q.service}</td>
                    <td className="px-5 py-4 text-[#0A192F]/60 font-light max-w-xs truncate">{q.message || "—"}</td>
                    <td className="px-5 py-4 text-[#0A192F]/50 font-light text-xs">{(q.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
